import { DartFormat } from '@/features/ai-dart/format';
import type { TicketEvent } from '@/features/ai-dart/types';

/**
 * detail을 사람이 읽을 문자열로. **값이 없으면 `null`을 돌려준다.**
 *
 * `format.ts`의 `DartFormat.json`을 쓰지 않는 이유: `JSON.stringify(null)`이 문자열 `'null'`이라 truthy다.
 * 그대로 렌더하면 detail 없는 이벤트마다 `null`만 담긴 코드 블록이 찍힌다(실제로 그랬다).
 */
function detailText(detail: unknown): string | null {
  if (detail === null || detail === undefined) return null;
  if (typeof detail === 'string') return detail.trim() || null;
  if (typeof detail === 'object' && Object.keys(detail as object).length === 0) return null;
  return JSON.stringify(detail, null, 2);
}

export function TicketTimeline({ events }: { events: TicketEvent[] }): React.JSX.Element {
  if (events.length === 0) return <p className="text-[12px] text-faint">기록된 이벤트가 없습니다.</p>;
  return (
    // 왼쪽 레일 + 도트로 시간 축을 만든다. 도트가 없으면 항목 경계가 안 보여 한 덩어리로 읽힌다.
    <ol className="ml-1 list-none border-l border-line pl-0">
      {events.map((event, index) => {
        const text = detailText(event.detail);
        return (
          <li key={`${event.ts}-${index}`} className="relative py-2.5 pl-5">
            <span className="absolute top-[15px] -left-[4.5px] size-2 rounded-full bg-accent ring-2 ring-surface" />
            <div className="flex flex-wrap items-baseline gap-x-2">
              <strong className="text-[12.5px]">{event.kind}</strong>
              <span className="text-[11px] text-faint">{event.actor}</span>
              <span className="text-[11px] text-faint">· {DartFormat.kst(event.ts)}</span>
            </div>
            {text && (
              <pre className="mt-1.5 overflow-auto rounded-md bg-line-soft px-2.5 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-muted">
                {text}
              </pre>
            )}
          </li>
        );
      })}
    </ol>
  );
}
